/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 9 hook(s) need dependency review; fix each and remove this directive */
import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import { useTranslation } from "@i18n";
/* eslint no-sequences: 0 */

import { useEffect, useState, Suspense, lazy, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { getCommentsList } from "@Features/issues/state/bcfData.actions";
import { getFileVersionData, getFileVersions, setCurrentFileVersionId } from "@Actions/fileUpload";
import bcfConverter from "@Services/bcfConverter";
import "./DesignViewer.css";
import { getTransmittals, getTransmittalFileVersionsForSpace } from "@Features/transmittals";
import DesignViewerHeader from "../DesignViewerHeader";
import {
  DRAWING_TYPE,
  BIM_ENGINE,
  CAD_ENGINE,
  PDF_ENGINE,
  SHEET_ENGINE,
  IMAGE_ENGINE,
  DOCUMENT_ENGINE,
  PANORAMA_ENGINE,
  VIDEO_ENGINE,
  HTML_ENGINE,
  PLATFORM,
} from "@Services/constants";
import { getEstimateData } from "@Actions/EstimateList";
import useLoadExternalScripts from "./useLoadExternalScripts";
import useResetDesignViewer from "./useResetDesignViewer";
import { setAccessPermission, selectFileAccessFor } from "@Reducers/AccessPermissionReducer";
import { setModalStatus, setSettingModalAPIData, setViewerState } from "@Reducers/Viewer";
import { getViewerUrl } from "@Helpers";
import img403 from "@Assets/images/noAccess.svg";
import { setIsFileAnOlderVersion } from "@Reducers/designViewer";
import LoaderScreen from "@Components/Loader";
// Recipient share view (CP-283): this same page serves someone with no CUBE session at
// /share/:token/file/:fileId, so the session-bound reads and the collaboration features below
// are gated on it.
import { getShareViewerSession, isShareViewerSession } from "@Features/external-share/shareViewerSession";
import { _defaultUserSettings } from "@Helpers/viewerSettings.constant";
import {
  GuestAccountPanel,
  SHARE_SIGN_IN_HREF,
  SHARE_SIGN_UP_HREF,
  ShareViewerNavigationHint,
} from "@Features/external-share";
import { recentAccessConstant } from "@Components/RecentAccess/recentAccessConstant.js";
import axiosWrapper from "@Services/axiosWrapper";
import type { GetSpaceUsersListResponse } from "@ServerTypes/spaces/space.schema";
import type { RecentAccessResponse } from "@ServerTypes/recentAccess/recentAccess.schema";
import _ from "lodash";
import GeoSpatial from "@Components/GeoSpatial";
import EmbeddedViewer from "@Features/design-viewer/components/EmbeddedViewer";
import { getNodeIdAndUniqueIdMap, getSettingModalData } from "@Actions/Viewer";
import { clearOtherNodeIdAndUniqueIdCacheMaps } from "@Services/utils/nodeIdAndUniqueIdMapDB";
import * as Sentry from "@sentry/react";
import { resolveFileVersionIds } from "@Services/utils/resolveFileVersionIds";
import { viewableFileVersionIdsFrom } from "@Services/utils/fileViewPermission";
import Text from "@CubeComponents/html/Text/Text";
import IconComponent from "@CubeComponents/icons";
import type { FileVersionData, SourceFileVersionData } from "@Types/fileOperations";
import type { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import { getSpaceUsersList } from "@Services/dataManager";
import { setSpacesInProjectList } from "@Reducers/projectSetting";
import { runWithConcurrency } from "@Services/utils/helpers";
import type { ConstituentFilesProcessingStatus, FileVersionDropDownItem } from "../DesignViewerHeader";
import type { HoopsViewerHandle } from "../DesignViewerLayoutSetting";

/**
 * A row of `state.fileVersions[fileId]`. The slice declares its items as `SourceFileVersionData`,
 * which omits the two members the merged-file branch of the version dropdown reads.
 */
type FileVersionRow = SourceFileVersionData & {
  resourceType?: string;
  sourceFileId?: { awsFileName?: string }[];
};

/**
 * A row of `state.transmittalVersions`.
 *
 * SCHEMA GAP: `getTransmittalFileVersionsForSpaceResponse` declares `matchingVersion` with only
 * `_id`/`createdDate`/`fileVersionNumber`/`size` and `.strip()`s everything else, yet the version
 * dropdown below reads `matchingVersion.awsFileName` off it.
 */
type TransmittalVersionRow = RootState["transmittalVersions"][number] & {
  matchingVersion?: { awsFileName?: string };
};

/** The issue objects handed to `bcfConverter`, plus the comments map this component attaches. */
type BcfIssueEntry = { comments?: Record<string, unknown>; [key: string]: unknown };

/** Payload for `POST /recentAccess/recentAccess`; `metaData` is merged with the URL query below. */
type RecentAccessPayload = {
  module: string;
  metaData: Record<string, unknown>;
  /** `projectData.id` is optional on the slice and is passed through unguarded. */
  projectId: string | undefined;
  platform: string;
};

const HoopsViewer = lazy(() => import("@Features/viewer-core/components/Viewer/Viewer"));
const PdfComparisonViewer = lazy(() => import("@Features/pdf-viewer/components/PdfComparison/PdfComparisonViewer"));
const SidebarViewer = lazy(() => import("@Components/StickySidebarViewer/SidebarViewer.jsx"));
const OfficeViewer = lazy(() => import("@Components/OfficeViewer"));
// Legacy document/spreadsheet viewers — rendered when Office is gated off (prod & non-staging/qa envs).
const Document = lazy(() => import("@Components/Document/Document"));
const Spreadsheet = lazy(() => import("@Features/spreadsheet-viewer/components/Spreadsheet"));
const Panorama = lazy(() => import("@Features/design-viewer/components/Panorama/Panorama"));
const Video = lazy(() => import("@Components/Video"));
const HtmlViewer = lazy(() => import("@Features/design-viewer/components/HtmlViewer"));

// The Office (Collabora) viewer is intentionally limited to environments where the Office backend +
// infra are provisioned. Only staging and qa are enabled; prod and every other environment must NOT
// open the Office editor (documents/spreadsheets fall back to their prior, no-Office behaviour).
const OFFICE_ENABLED = ["staging", "qa", "prod"].includes(process.env.REACT_APP_ENV ?? "");

function DesignViewer() {
  const { t } = useTranslation();
  const viewerState = useSelector((state: RootState) => state.viewerState);
  const getAccessPermission = useSelector((state: RootState) => state.getAccessPermission);
  const estimateData = useSelector((state: RootState) => state.estimateData);
  const HoopsModalStatus = useSelector((state: RootState) => state.HoopsModalStatus);
  const projectData = useSelector((state: RootState) => state.projectData);
  const fileVersions = useSelector((state: RootState) => state.fileVersions);
  const issues = useSelector((state: RootState) => state.issues);
  const transmittals = useSelector((state: RootState) => state.transmittals);
  const transmittalVersions = useSelector((state: RootState) => state.transmittalVersions);
  const filesData = useSelector((state: RootState) => state.filesData as Record<string, FileVersionData>);

  const location = useLocation();
  /**
   * External entry point: the `:fileId` route param is the file VERSION id.
   *
   * NOT asserted present. `useParams` yields `string | undefined` for every param, and the previous
   * `as { fileId: ObjectIdString }` claimed both presence and the branded shape — which is precisely how
   * a route declaring a DIFFERENTLY NAMED param (`/share/:token/file/:fileVersionId`) compiled cleanly
   * and then failed at runtime, deep inside a reducer: `setCurrentFileVersionId(undefined)` on a
   * string-initialised slice throws "A case reducer on a non-draftable value must not return undefined".
   *
   * Collapsing an absent param to `""` matches the slice's own `initialState` and keeps `undefined` out
   * of every downstream consumer. Any route mounting this component must name the segment `fileId`.
   */
  const routeParams = useParams<{ fileId: string }>();
  const params = { fileId: routeParams.fileId ?? "" };

  /**
   * Report a missing param instead of rendering a blank viewer.
   *
   * Collapsing `undefined` to `""` above stops the crash, but it would also make the SAME mistake silent
   * next time — a route naming the segment anything other than `fileId` would just show an empty viewer.
   * This is the loud-but-harmless middle ground: the screen stays up, the misconfiguration gets reported.
   */
  useEffect(() => {
    if (routeParams.fileId === undefined) {
      Sentry.captureException(
        new Error("DesignViewer mounted with no `fileId` route param — the route must name the segment `fileId`"),
      );
    }
  }, [routeParams.fileId]);
  const dispatch = useDispatch<AppDispatch>();
  /**
   * Recipient share view (CP-283): this page is being rendered for someone with NO CUBE session, at
   * `/share/:token/file/:fileId`.
   *
   * Everything gated on this falls into two groups. Reads that need a session (space users, viewer
   * settings, recent-access) would 401 — and because they go through `axiosWrapper`, a 401 can bounce
   * the recipient to the sign-in page, replacing the share with a login screen. Collaboration features
   * (comments, issues, transmittals, estimates, markup) are simply not part of what a link grants.
   *
   * Read from the module-level share session rather than a prop, because the same answer is needed by
   * non-React code in the viewer stack (`ViewerImpl`) and by Redux thunks.
   */
  const isShareView = isShareViewerSession();
  const isFileAnOlderVersion = useSelector((state: RootState) => state.isFileAnOlderVersion);

  const [isMeasurementsTabOpen, setIsMeasurementsTabOpen] = useState<boolean>(false);
  const [selectedIssues, setSelectedIssues] = useState<Record<string, boolean>>({});
  const [fileType, setFileType] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [engineType, setEngineType] = useState<string>("");
  const [embedUrl, setEmbedUrl] = useState<string>("");
  const [currentViewIssueId, setCurrentViewIssueId] = useState<string>("");
  const [showWarning, setShowWarning] = useState<boolean>(true);
  // Genuinely a union: the transmittal effects below seed it with "" before a row supplies the
  // numeric index, and both values reach the header verbatim.
  const [currentVersionNumber, setCurrentVersionNumber] = useState<number | string>(0);
  const [latestTransmittalFileVersionId, setLatestTransmittalFileVersionId] = useState<string>("");
  const [transmittalId, setTransmittalId] = useState<string>("");
  const [isPageOpenedThroughTransmittal, setIsPageOpenedThroughTransmittal] = useState<boolean | null>(null);
  const [versionDropDownData, setVersionDropDownData] = useState<FileVersionDropDownItem[]>([]);
  const [comparedFileModel, setComparedFileModel] = useState<string[] | null>(null);
  const [comparedVersion, setComparedVersion] = useState<string | null>(null);
  const [fileId, setFileId] = useState<string>("");
  const [fileVersionId, setFileVersionId] = useState<string>(params.fileId);
  const [fileVersionData, setFileVersionData] = useState<FileVersionData | null>(null);
  const [HoopsViewerData, setHoopsViewerData] = useState<HoopsViewerHandle | null>(null);
  const urlSearchParams = new URLSearchParams(location.search);
  const skipProperties = urlSearchParams.get("mode") === "skipProperties";
  // External entry point: the `spaceId` query param. Adopted once here, at its declaration — the
  // transmittal flow that consumes it is only reachable with the param present (the backend route
  // schema validates it).
  const spaceIdParam = urlSearchParams.get("spaceId") as ObjectIdString;
  const [isUnrealPlugin, setIsUnrealPlugin] = useState<boolean>(false);
  const [canViewFile, setCanViewFile] = useState<boolean | null>(null);
  const [modalToOpen, setModalToOpen] = useState<string[]>([]);
  const [constituentFilesProcessingStatus, setConstituentFilesProcessingStatus] =
    useState<ConstituentFilesProcessingStatus | null>(null);
  const scheduleVersionId = urlSearchParams.get("scheduleVersionId");
  const [newVersionTagVisible, setNewVersionTagVisible] = useState<boolean>(true);

  const [conversionProgress, setConversionProgress] = useState<string>("NOT_STARTED");
  const [intervalForConversion, setIntervalForConversion] = useState<ReturnType<typeof setInterval> | null>(null);
  const [fileVersionIdForInterval, setFileVersionIdForInterval] = useState<string>("");
  /** Mirror of `intervalForConversion` for the unmount cleanup, which must see the CURRENT id. */
  const intervalForConversionRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    intervalForConversionRef.current = intervalForConversion;
  }, [intervalForConversion]);
  useEffect(() => {
    // Unmount-scoped: without this the 10s conversion poll outlives the component — every Back
    // navigation (and every file-to-file switch in a shared folder, which remounts this component)
    // leaked one interval that kept dispatching getFileVersionData for the tab's lifetime.
    return () => {
      if (intervalForConversionRef.current) clearInterval(intervalForConversionRef.current);
    };
  }, []);
  const [showLoader, setShowLoader] = useState<boolean | null>(null);
  const premissionsData = _.get(getAccessPermission, "data", null) as { write?: boolean } | null;
  // Only the result for the file now open; undefined while a switch is resolving.
  const fileAccess = useSelector((state: RootState) => selectFileAccessFor(state, fileVersionId));
  // Props for the legacy Spreadsheet viewer (used when Office is gated off).
  const sheetProps = {
    showSheetTabs: true,
    allowInsert: !!premissionsData?.write,
    allowDelete: !!premissionsData?.write,
    allowEditing: !!premissionsData?.write,
  };

  useEffect(() => {
    console.log("Permission is " + premissionsData?.write);
  }, [premissionsData?.write]);

  const resetViewer = useResetDesignViewer();
  const resetDesignViewer = () => {
    resetViewer();
    clearInterval(intervalForConversion!);
  };

  useEffect(() => {
    dispatch(setCurrentFileVersionId(fileVersionId));
    setConstituentFilesProcessingStatus(null);
    setFileVersionData(null);
    setFileType("");
    setEngineType("");
    setEmbedUrl("");
    setShowLoader(null);
    setConversionProgress("NOT_STARTED");
  }, [fileVersionId]);

  useEffect(() => {
    if ((getAccessPermission as { canViewFile?: boolean }).canViewFile) {
      setCanViewFile(true);
    } else if ((getAccessPermission as { canViewFile?: boolean }).canViewFile === false) {
      setCanViewFile(false);
    }
  }, [getAccessPermission]);

  const [externalScriptsLoaded] = useLoadExternalScripts();

  /**
   * VIEWER-CORE GAP: the shell's `parentCallback` payload is not an exported type, and it declares
   * `rcDockref` with viewer-core's local `DockLayoutHandle`, which has only `find()` — not rc-dock's
   * `saveLayout()`, the one member the layout manager calls on this handle. Taken as `unknown` (so
   * the prop stays assignable) and adopted with a single erased assertion.
   */
  const handleHoopsViewerCallback = (childData: unknown) => {
    setHoopsViewerData(childData as HoopsViewerHandle);
  };

  const handleSetIssueStage = (currentStage: string, backStage?: string) => {
    dispatch(
      setModalStatus({
        ...HoopsModalStatus,
        Issues: {
          ...HoopsModalStatus.Issues,
          currentStage,
          backStage: backStage || (_.get(HoopsModalStatus, "Issues.defaultStage") as string | undefined),
        },
      }),
    );
  };

  // The `filesData` slice is keyed by file version id and merges entries in place, so an unrelated
  // file's arrival leaves this entry's identity untouched. Every effect below that only needs the
  // file now open depends on this entry rather than on the whole slice — otherwise loading a merged
  // file (100+ constituents) re-runs them once per constituent.
  const currentFileEntry = filesData[fileVersionId];

  useEffect(() => {
    if (currentFileEntry) {
      setFileVersionData(currentFileEntry);
      // QA r5 #9: a recipient's tab is named after WHAT was shared, not the app shell. QA r8 #2: named
      // after the SHARED item (folder or file the link was made on), so it stays put while the
      // recipient opens files inside a shared folder; the open file's own name is only the fallback
      // for a session that carries none.
      document.title = isShareView
        ? `${getShareViewerSession()?.itemName ?? currentFileEntry.fileName} | Shared Link | CUBE`
        : `${currentFileEntry.fileName} - Design Viewer`;
    }
  }, [currentFileEntry, isShareView]);

  useEffect(() => {
    // A recipient reaches none of these: the URL has no transmittal/estimate/measurement params, and
    // `getSettingModalData` is a per-user read that would 401. Returning early keeps the whole block
    // out of the share path rather than guarding each dispatch.
    if (isShareView) {
      setIsPageOpenedThroughTransmittal(false);
      // A guest cannot call /accounts/get, but the viewer's init gates (Viewer.tsx) refuse to
      // construct HOOPS until settings exist, and applySettings dereferences several values into
      // HOOPS setters that CRASH on undefined (selection/measure/sheet colors). Seeding the SAME
      // defaults a fresh account gets — the shared constant the server itself seeds accounts from —
      // opens the gates with values that are complete by construction.
      dispatch(setSettingModalAPIData(_defaultUserSettings()));
      return;
    }
    const urlSearchParams = new URLSearchParams(location.search);
    if (urlSearchParams.get("measurementId")) {
      setIsMeasurementsTabOpen(true);
    }

    if (urlSearchParams.get("transmittalId")) {
      setTransmittalId(urlSearchParams.get("transmittalId") as string);
      setIsPageOpenedThroughTransmittal(true);

      dispatch(
        getTransmittals({
          transmittalVersionId: urlSearchParams.get("transmittalId") ?? undefined,
        }),
      );
    } else {
      setIsPageOpenedThroughTransmittal(false);
    }

    if (urlSearchParams.get("plugin") == "unreal") {
      setIsUnrealPlugin(true);
    }
    // External entry point: URL query params. Both ids are adopted once here, at their
    // declaration (the backend route schema validates them).
    const estimateIdParam = urlSearchParams.get("estimateId") as ObjectIdString | null;
    const versionIdParam = urlSearchParams.get("versionId") as ObjectIdString | null;
    if (estimateIdParam) {
      dispatch(
        getEstimateData({
          estimateId: estimateIdParam,
          versionId: versionIdParam || undefined,
        }),
      );
    }
    dispatch(getSettingModalData());
  }, [location.search, isShareView]);

  useEffect(() => {
    handleFileVersionIdChange(params.fileId);

    const searchParams = new URLSearchParams(location.search);
    const issueId = searchParams.get("issueId");
    const modalToOpen: string[] = [];

    // Share mode ignores these entirely: ?issueId / ?clashTestId are hand-editable, and honouring
    // them would open the Issues / Clash docks, whose fetches are authenticated. The server refuses
    // the data either way — this only stops a recipient (or a prober) turning the URL bar into a
    // self-inflicted broken panel.
    if (!isShareView) {
      if (issueId) {
        setCurrentViewIssueId(issueId);
        modalToOpen.push("Issues");
      }
      if (searchParams.get("clashTestId")) {
        modalToOpen.push("Clash Tests");
      }
    }

    setModalToOpen(modalToOpen);
    // Everything read here comes from the query string and the route param, so the whole `location`
    // (whose identity changes on every navigation, including a re-push of the same URL) would only
    // re-seed `fileVersionId`/`modalToOpen` with values it already holds.
  }, [location.search, params.fileId]);

  useEffect(() => {
    if (fileVersionId) {
      dispatch(
        getFileVersionData({
          fileVersionId,
        }),
      );
    }
  }, [fileVersionId]);

  const currentFileVersionDataId = filesData[fileVersionId]?.id;
  useEffect(() => {
    const fileVersionData = filesData[fileVersionId];
    if (
      fileType &&
      (DRAWING_TYPE.includes(fileType) || _.get(fileVersionData, "resourceType") == "RESOURCE_TYPE_MERGED_FILE") &&
      !skipProperties
    ) {
      // For a merged file this fans out over every constituent (~100+). Give the
      // fan-out an AbortController so leaving the viewer mid-load (Back / reload /
      // file switch) cancels the in-flight map fetches + unzips instead of letting
      // them run to completion and block the navigation.
      const controller = new AbortController();
      const { signal } = controller;
      clearOtherNodeIdAndUniqueIdCacheMaps(resolveFileVersionIds(filesData, fileVersionId));
      if (fileVersionData?.resourceType == "RESOURCE_TYPE_MERGED_FILE") {
        const activeSourceFiles = (fileVersionData.sourceFileVersionId ?? []).filter(
          (sourceFile: SourceFileVersionData) => !sourceFile.isDeleted,
        );
        void runWithConcurrency(
          activeSourceFiles.map(
            (sourceFile: SourceFileVersionData) => () =>
              dispatch(getFileVersionData({ fileVersionId: sourceFile.id }, signal)),
          ),
          5,
          signal,
        ).catch((err: unknown) => {
          if (!signal.aborted) console.error("Failed to load source file version data", err);
          Promise.reject(err);
        });
      }
      return () => controller.abort();
    }
    // Deliberately NOT dependent on fileAccess: this fan-out is un-gated metadata and
    // must not be repeated every time the permission result changes identity (the
    // 10s conversion poll re-dispatches it).
  }, [currentFileVersionDataId, fileType]);

  // nodeId/uniqueId maps, gated on this file's own isFile403 answer. Waits rather
  // than falling back while the check is in flight (fileAccess undefined), so a
  // restricted constituent is never fetched — /View answers such a request with a
  // 200 + plain-text body that unzip() then throws on.
  useEffect(() => {
    // Never for a recipient: the map fetch is authenticated, and the maps only feed selection, issue
    // pins and the properties panel — all absent in the Navigate-only share view. (The share access
    // result makes `fileAccess` truthy here, so without this gate the fetch WOULD fire and 401.)
    if (isShareView) return;
    const fileVersionData = filesData[fileVersionId];
    const resourceType = _.get(fileVersionData, "resourceType");
    const isRelevant =
      fileType && (DRAWING_TYPE.includes(fileType) || resourceType == "RESOURCE_TYPE_MERGED_FILE") && !skipProperties;
    if (!isRelevant || !fileAccess) return;

    const controller = new AbortController();
    const { signal } = controller;
    // status:false means the check itself failed — fall back to the un-gated list so a
    // transient failure degrades instead of leaving the model without its maps.
    const viewableIds = viewableFileVersionIdsFrom(fileAccess, filesData, fileVersionId);
    if (viewableIds.length) {
      void runWithConcurrency(
        viewableIds.map((id) => () => getNodeIdAndUniqueIdMap(id, signal)),
        5,
        signal,
      ).catch((err: unknown) => {
        if (signal.aborted) return;
        console.error("Failed to load nodeId/uniqueId maps", err);
        Sentry.captureException(err);
      });
    }
    return () => controller.abort();
  }, [currentFileVersionDataId, fileType, fileAccess]);

  const getUsersInSpaces = (data: { spaceId: ObjectIdString }) => {
    return (dispatch: AppDispatch) => {
      axiosWrapper.post<GetSpaceUsersListResponse>(getSpaceUsersList, data).then((response) => {
        dispatch(setSpacesInProjectList(response.data.result));
      });
    };
  };

  useEffect(() => {
    if (fileVersionData && fileVersionId) {
      if (isPageOpenedThroughTransmittal === true) {
        dispatch(
          getTransmittalFileVersionsForSpace({
            resourceType: fileVersionData.resourceType,
            fileId: fileVersionData.fileId,
            spaceId: spaceIdParam,
          }),
        );
      }
      // `getFileVersions` is an AUTHENTICATED route. Left ungated it 401s for a recipient — and a 401
      // through axiosWrapper does not merely fail, it can bounce them to the sign-in page. A recipient
      // also gets no version switcher (see the header's `isShareView` gate): a share authorises exactly
      // one item, and its superseded revisions are a wider disclosure than the link was granted for.
      if (!isShareView) {
        dispatch(
          getFileVersions({
            resourceType: fileVersionData.resourceType,
            fileId: fileVersionData.fileId,
          }),
        );
      }
      // Space members drive comment/issue attribution, which a recipient does not get — and the read
      // itself needs a session, so for a share it would 401 rather than simply returning nothing.
      if (!isShareView) {
        dispatch(
          getUsersInSpaces({
            spaceId: fileVersionData.spaceId,
          }),
        );
      }
    }
  }, [fileVersionData, fileVersionId, isPageOpenedThroughTransmittal, isShareView]);

  useEffect(() => {
    if (!fileVersionData) return;
    setFileId(fileVersionData.fileId);
    // if (!path.includes('transmittalId') && !path.includes('issueId') && !path.includes('taskId')) {
    if (fileVersionData?.fileId) {
      // The thunk only declares `fileVersionId`/`spaceId`; the other members are carried by this
      // call site. `spaceId` is a route query param and is consumed unguarded — erased assertion.
      let data: Parameters<typeof setAccessPermission>[0] & {
        fileId?: string;
        resourceType?: string;
        transmittalId?: string | null;
      } = {
        fileId: fileVersionData.fileId,
        resourceType: fileVersionData.resourceType,
        spaceId: urlSearchParams.get("spaceId") as string,
        fileVersionId,
      };
      if (urlSearchParams.get("transmittalId")) {
        data = {
          ...data,
          transmittalId: urlSearchParams.get("transmittalId"),
        };
      }

      dispatch(setAccessPermission(data));
    }
    // Keyed on the identifiers the answer actually depends on, NOT on fileVersionData:
    // the 10s conversion poll replaces that object every tick, which would re-dispatch
    // and churn fileAccess identity, re-running every consumer that depends on it.
  }, [fileVersionId, fileVersionData?.fileId, urlSearchParams.get("spaceId"), urlSearchParams.get("transmittalId")]);

  useEffect(() => {
    if (fileVersionData && fileVersionData.fileName) {
      const fileTypeFromName = fileVersionData.awsFileName.split(".").pop() ?? "";
      setFileType(fileTypeFromName.toLowerCase());
      setFileName(fileVersionData.fileName);
      setEngineType(fileVersionData.engineType ?? "");
      setEmbedUrl(fileVersionData.embedUrl ?? "");
    }
  }, [fileVersionData, filesData]);

  useEffect(() => {
    dispatch(
      setViewerState({
        currentFileId: fileId,
        currentVersionId: fileVersionId,
        compareVersionId: comparedVersion,
      }),
    );
  }, [fileId, comparedVersion, fileVersionId, comparedFileModel]);

  useEffect(() => {
    if (versionDropDownData.length && transmittals && transmittalId) {
      // KNOWN BUG, preserved verbatim: this starts as "" yet feeds a `useState<number>` setter; in
      // practice it is only dispatched after a matching transmittal row has assigned the index.
      let currentVersionNumber: number | string = "";
      transmittalVersions.forEach((val: TransmittalVersionRow) => {
        if (val.transmittalId === transmittalId) {
          currentVersionNumber = val.indexAtProjectLevel!;
          setCurrentVersionNumber(currentVersionNumber);
        }
      });
      dispatch(setIsFileAnOlderVersion(latestTransmittalFileVersionId !== fileVersionId));
    }
  }, [transmittalId]);

  useEffect(() => {
    if (isPageOpenedThroughTransmittal === false) {
      const fileVersionsData = fileVersionData ? fileVersions[fileVersionData.fileId] : undefined;
      if (fileVersionData && fileVersionsData) {
        const currentFileVersion = fileVersionsData.find((file: FileVersionRow) => file.id == fileVersionId);
        const fileVersionNumber = currentFileVersion?.fileVersionNumber ?? fileVersionData.fileVersionNumber;

        if (fileVersionNumber != null) {
          dispatch(setIsFileAnOlderVersion(fileVersionNumber < fileVersionsData.length));
          setCurrentVersionNumber(fileVersionNumber);
        } else {
          dispatch(setIsFileAnOlderVersion(false));
        }
      }
    } else if (isPageOpenedThroughTransmittal === true) {
      if (fileVersionData && fileVersions[fileId] && transmittalVersions) {
        if (transmittals) {
          // Same "" -> `useState<number>` mismatch as the effect above; preserved verbatim.
          let currentVersionNumber: number | string = "";
          let outputArray: FileVersionDropDownItem[] = [];
          const latestTransmittalAttachedFileVersionId = transmittalVersions.reduce(
            (maxVersionId: TransmittalVersionRow["matchingVersion"] | null, file: TransmittalVersionRow) => {
              // `undefined > n` is `false`, which is the intent; the assertion only silences the
              // comparison against the schema's optional `fileVersionNumber`.
              return file?.matchingVersion?.fileVersionNumber! > (maxVersionId?.fileVersionNumber || 0)
                ? file.matchingVersion
                : maxVersionId;
            },
            null,
          )?._id;
          const currentTransmittalId = transmittalId;
          setLatestTransmittalFileVersionId(latestTransmittalAttachedFileVersionId!);
          transmittalVersions.forEach((val: TransmittalVersionRow) => {
            if (val.transmittalId === currentTransmittalId) {
              currentVersionNumber = val.indexAtProjectLevel!;
              setCurrentVersionNumber(currentVersionNumber);
            }
            outputArray.push({
              text: `TS-${val.indexAtProjectLevel} (V${val?.matchingVersion?.fileVersionNumber})`,
              url: getViewerUrl({
                fileVersionId: val.resourceVersionId,
                transmittalVersionId: val.transmittalId,
                projectId: projectData.id,
                spaceId: urlSearchParams.get("spaceId") || undefined,
              }) as string,
              createdDate: val.createdDate!,
              versionNumber: val.indexAtProjectLevel!,
              fileVersionId: val.resourceVersionId!,
              awsFileName: val?.matchingVersion?.awsFileName!.split(".")[0]!,
            });
          });
          outputArray = outputArray.sort((a, b) => b.createdDate - a.createdDate);
          setVersionDropDownData(outputArray);
          dispatch(setIsFileAnOlderVersion(latestTransmittalAttachedFileVersionId !== fileVersionId));
          setCurrentVersionNumber(currentVersionNumber);
        }
      }
    }
  }, [isPageOpenedThroughTransmittal, fileVersions, fileVersionData, transmittalVersions, transmittals, fileId]);

  // useEffect(() => {
  //   if (bcfData.bcfData && bcfData.bcfData.Body) {
  //     initialiseBcfFromBuffer();
  //   }
  // }, [bcfData.bcfData]);

  // const initialiseBcfFromBuffer = () => {
  //   // TODO: viewer will not be availabel here. use viewerImpl. SKipping due to time constraint
  //   viewer.current?.BCFManager.addBCFFromBuffer(bcfData.bcfData.Body.data)
  //     .then(() => {
  //       dispatch(setAutoBcfLoaded(true));
  //     })
  //     .catch((e: unknown) => {
  //       delayPromise(500).then(() => {
  //         initialiseBcfFromBuffer();
  //       });
  //     });
  // };

  // The viewer shell declares this callback's payload as `{ issues: unknown; selectedIssues: unknown }`,
  // so both members are narrowed here rather than in the signature, which has to stay assignable.
  const handleExportToBcf = async ({ issues, selectedIssues }: { issues: unknown; selectedIssues: unknown }) => {
    const issuesData = _.cloneDeep(
      Object.keys(issues as Record<string, BcfIssueEntry>)
        .filter((val: string) => (selectedIssues as Record<string, boolean>)[val])
        .reduce(
          (res: Record<string, BcfIssueEntry>, curr) => (
            (res[curr] = (issues as Record<string, BcfIssueEntry>)[curr]!),
            res
          ),
          {},
        ),
    );

    // `Object.keys` always erases the key type, and `selectedIssues` is untyped state keyed by
    // issue _id, so the id list is adopted once here, at its declaration.
    const selectedIssueIds = Object.keys(selectedIssues as Record<string, boolean>) as ObjectIdString[];

    await Promise.all(
      selectedIssueIds.map(async (val) => {
        const response = await getCommentsList({ issueId: val });
        const commentsData = response.data.data ?? [];
        issuesData[val]!.comments = commentsData.length
          ? commentsData.reduce(
              (res: Record<string, unknown>, curr: (typeof commentsData)[number]) => (
                (res[curr._id as string] = curr),
                res
              ),
              {},
            )
          : {};
      }),
    );
    bcfConverter(issuesData);
  };

  const handleDelete = () => {
    // Delete issues logic
  };

  const handleSelectedIssues = (issueId: string, deleteAll: boolean = false) => {
    if (deleteAll) {
      setSelectedIssues({});
    } else {
      setSelectedIssues((prev: Record<string, boolean>) => {
        if (prev[issueId]) {
          delete prev[issueId];
        } else {
          prev[issueId] = true;
        }
        return { ...prev };
      });
    }
  };

  const onViewIssueClick = (issueId: string) => {
    setCurrentViewIssueId(issueId);
    handleSetIssueStage("view_issue");
  };

  const handleCloseOlderVersionWarning = () => {
    setShowWarning(false);
  };

  const getDropdownForNormalFiles = () => {
    let outputArray: FileVersionDropDownItem[] = [];

    if (fileVersionData && fileVersions[fileId]) {
      let fileVersionsData = fileVersions[fileId];
      // Every member below is optional on `SourceFileVersionData` and is consumed unguarded here;
      // the assertions are erased, fallbacks would change behaviour.
      const dropdownData = fileVersionsData.map((fileVersion: FileVersionRow) => {
        let fileName = fileVersion.awsFileName!.split(".")[0]!;
        if (fileVersion.resourceType == "RESOURCE_TYPE_MERGED_FILE") {
          fileName = fileVersion
            .sourceFileId!.map((val: { awsFileName?: string }) => val.awsFileName!.split(".")[0])
            .join(",");
        }

        return {
          text: `V${fileVersion.fileVersionNumber}`,
          url: `/designviewer/file/${fileVersion.id}`,
          createdDate: fileVersion.createdDate!,
          versionNumber: fileVersion.fileVersionNumber!,
          awsFileName: fileName,
          fileVersionId: fileVersion.id,
        };
      });
      outputArray = dropdownData.sort(
        (a: FileVersionDropDownItem, b: FileVersionDropDownItem) => a.versionNumber - b.versionNumber,
      );
    }
    return outputArray;
  };

  const getDropdownForFilesSharedThroughTransmittal = () => versionDropDownData;

  const getFileVersionsDropDownItems = () => {
    if (isPageOpenedThroughTransmittal === false) {
      return getDropdownForNormalFiles();
    }
    if (isPageOpenedThroughTransmittal === true) {
      return getDropdownForFilesSharedThroughTransmittal();
    }
  };

  const handleComparedFileModelChange = (val: string[]) => {
    setComparedFileModel(val);
  };

  const handleComparedFileVersionChange = (val: string) => {
    setComparedVersion(val);
  };

  const handleFileVersionIdChange = (val: string) => {
    setFileVersionId(val);
  };

  const setIntervalForFileProcessing = () => {
    const interval = setInterval(() => {
      dispatch(
        getFileVersionData({
          fileVersionId,
        }),
      );
    }, 10000);
    setFileVersionIdForInterval(fileVersionId);
    setIntervalForConversion(interval);
  };

  const getConstituentFilesProcessingStatus = (fileEntry: FileVersionData) => {
    const sourceFiles = fileEntry.sourceFileVersionId ?? [];
    let inProgressCount = 0;
    let erroredCount = 0;
    let completedCount = 0;

    sourceFiles.forEach((file: SourceFileVersionData) => {
      switch (file.conversionStatus) {
        case "IN_PROGRESS":
        case "NOT_STARTED":
          inProgressCount += 1;
          break;
        case "ERRORED":
          erroredCount += 1;
          break;
        case "COMPLETED":
          completedCount += 1;
          break;
      }
    });

    if (erroredCount > 0) {
      return {
        status: "ERROR",
        count: erroredCount,
        message: `There were errors in processing ${erroredCount} file(s). Please retry the conversion process for them.`,
        buttonContent: "View Constituent Files",
      };
    }
    if (inProgressCount > 0) {
      return {
        status: "PROCESSING",
        message:
          "Some constituent file(s) are still processing. They will not show up until the process is completed. Please refresh this page to see the latest changes once the processing has been completed.",
        buttonContent: "Refresh",
      };
    }
    if (completedCount === sourceFiles.length) {
      return { status: "COMPLETED" };
    }

    return null;
  };

  useEffect(() => {
    if (currentFileEntry?.resourceType == "RESOURCE_TYPE_MERGED_FILE") {
      const status = getConstituentFilesProcessingStatus(currentFileEntry);
      setConstituentFilesProcessingStatus(status);
    }
  }, [currentFileEntry]);

  useEffect(() => {
    const fileEntry = currentFileEntry;
    if (fileEntry) {
      const isMergedFile = fileEntry.resourceType === "RESOURCE_TYPE_MERGED_FILE";
      const mergedFileStatus = isMergedFile ? getConstituentFilesProcessingStatus(fileEntry)?.status : null;
      const conversionStatus = fileEntry.conversionStatus ?? "NOT_STARTED";
      const cachingStatus = fileEntry.cachingStatus ?? "NOT_STARTED";

      // Only conversion drives the 10s poll. `cachingStatus` tracked the office-caching worker, which is
      // retired, so it is frozen wherever it stood — NOT_STARTED on every office/HTML upload — and polling
      // on it never stopped (each tick replaced the version row, reloading an open HTML file).
      const isConversionNeeded = isMergedFile
        ? mergedFileStatus === "PROCESSING"
        : ["NOT_STARTED", "IN_PROGRESS"].includes(conversionStatus);

      if (isConversionNeeded) {
        if (intervalForConversion && fileVersionId != fileVersionIdForInterval) {
          clearInterval(intervalForConversion);
          setIntervalForFileProcessing();
        } else if (!intervalForConversion && fileVersionId != fileVersionIdForInterval) {
          setIntervalForFileProcessing();
        }
        if (isMergedFile || conversionStatus == "IN_PROGRESS") {
          setConversionProgress("IN_PROGRESS");
        }
      } else if (
        isMergedFile
          ? mergedFileStatus === "COMPLETED" || !mergedFileStatus
          : ["COMPLETED", "NOT_REQUIRED"].includes(conversionStatus)
      ) {
        if (intervalForConversion) {
          clearInterval(intervalForConversion);
        }
        setConversionProgress("COMPLETED");
      } else if ((isMergedFile && mergedFileStatus === "ERROR") || (!isMergedFile && conversionStatus === "ERRORED")) {
        if (intervalForConversion) {
          clearInterval(intervalForConversion);
        }
        setConversionProgress("ERRORED");
      }

      if (isMergedFile) {
        setShowLoader(mergedFileStatus === "PROCESSING");
      } else if (
        ["COMPLETED", "NOT_REQUIRED"].includes(conversionStatus) &&
        (fileEntry.cachingStatus === undefined || ["COMPLETED", "NOT_REQUIRED"].includes(cachingStatus))
      ) {
        setShowLoader(false);
      } else {
        setShowLoader(true);
      }
    }
    // `fileVersionId` stays: it is compared against `fileVersionIdForInterval` below, so it is read
    // in its own right and not only as the key into `filesData`.
  }, [currentFileEntry, fileVersionId]);

  const projectDataId = _.get(projectData, "id");
  const recentFileEntryId = _.get(filesData, `[${params.fileId}].id`);
  useEffect(() => {
    const recentFileEntry = filesData[params.fileId];
    if (recentFileEntry && _.get(projectData, "id")) {
      recentAccess({
        module: recentAccessConstant.file,
        metaData: {
          _id: params.fileId,
          name: recentFileEntry.fileName,
          projectId: projectData.id,
          spaceId: recentFileEntry.spaceId,
        },
        projectId: projectData.id,
        platform: PLATFORM.web,
      });
    }
  }, [recentFileEntryId, projectDataId]);

  const transmittalsId = _.get(transmittals, "_id");
  useEffect(() => {
    if (urlSearchParams.get("transmittalId") && _.get(transmittals, "_id") && _.get(projectData, "id")) {
      recentAccess({
        module: recentAccessConstant.transmittal,
        metaData: {
          _id: urlSearchParams.get("transmittalId"),
          name: transmittals.transmittalId,
          fileVersionId: params.fileId,
          projectId: projectData.id,
        },
        projectId: projectData.id,
        platform: PLATFORM.web,
      });
    }
  }, [transmittalsId, projectDataId]);

  const issuesEntryId = _.get(issues, `[${urlSearchParams.get("issueId")}].id`);
  useEffect(() => {
    const issueId = urlSearchParams.get("issueId");
    if (issueId && _.get(issues, `[${issueId}].id`) && _.get(projectData, "id")) {
      recentAccess({
        module: recentAccessConstant.issue,
        metaData: {
          _id: issueId,
          name: issues[issueId]!.title,
          fileVersionId: params.fileId,
          projectId: projectData.id,
        },
        projectId: projectData.id,
        platform: PLATFORM.web,
      });
    }
  }, [issuesEntryId, projectDataId]);

  function recentAccess(data: RecentAccessPayload) {
    // Recent-access is a per-USER trail. A recipient has no user to attribute it to, and the route is
    // session-bound, so the call is skipped rather than made and failed. Guarded inside the function so
    // all three call sites are covered by one check.
    if (isShareView) return;
    const searchParams: Record<string, string> = {};
    urlSearchParams.forEach((value: string, key: string) => {
      searchParams[key] = value;
    });
    axiosWrapper.post<RecentAccessResponse>(`${getBackendUrl()}/recentAccess/recentAccess`, {
      ...data,
      metaData: { ...data.metaData, ...searchParams },
    });
  }

  return (
    <>
      {externalScriptsLoaded && canViewFile && (
        <div
          className="flex h-full w-full overflow-auto bg-[#fafafa] p-7"
          style={{
            zoom: `${viewerState?.zoomLevel ? viewerState?.zoomLevel : 100}%`,
          }}
        >
          <div className="flex w-full flex-col rounded-lg shadow-[0_0_0.25rem_0_rgba(0,0,0,0.25)]">
            <DesignViewerHeader
              fileVersionsDropDownItems={getFileVersionsDropDownItems()}
              handleComparedFileModelChange={handleComparedFileModelChange}
              handleComparedFileVersionChange={handleComparedFileVersionChange}
              handleFileVersionIdChange={handleFileVersionIdChange}
              isPageOpenedThroughTransmittal={isPageOpenedThroughTransmittal}
              currentVersionNumber={currentVersionNumber}
              fileName={fileName}
              resetDesignViewer={resetDesignViewer}
              HoopsViewer={HoopsViewerData}
              currentfileVersionId={fileVersionId}
              engineType={engineType}
              modalToOpen={modalToOpen}
              enableDesignViewerLayoutManager={!showLoader}
              estimateId={urlSearchParams.get("estimateId") ? urlSearchParams.get("estimateId") : undefined}
              scheduleId={urlSearchParams.get("scheduleId") ? urlSearchParams.get("scheduleId") : undefined}
              scheduleVersionId={scheduleVersionId}
              constituentFilesProcessingStatus={constituentFilesProcessingStatus}
              setTransmittalId={setTransmittalId}
              isShareView={isShareView}
            />
            <div className="relative flex h-full overflow-hidden" data-testid="designViewerWrapper">
              {/* Model/drawing engines only: a PDF, image or spreadsheet needs no orbit instructions,
                  and the hint would be noise on them. */}
              {isShareView && !showLoader && [BIM_ENGINE, CAD_ENGINE].includes(engineType) && (
                <ShareViewerNavigationHint />
              )}
              {/* QA r4 #4: the "limited guest view" info affordance, bottom-left on EVERY engine.
                  pointer-events split so the trigger is clickable while canvas drags pass through
                  the wrapper; lifted above the PDF viewer's own bottom-left toolbar. */}
              {isShareView && !showLoader && (
                <div
                  className={`pointer-events-none absolute start-7 z-10 ${
                    [PDF_ENGINE].includes(engineType) ? "bottom-40" : "bottom-4"
                  }`}
                >
                  <div className="pointer-events-auto">
                    <GuestAccountPanel
                      signInHref={SHARE_SIGN_IN_HREF}
                      signUpHref={SHARE_SIGN_UP_HREF}
                      placement="bottom-start"
                      trigger="info"
                      limitsVariant
                    />
                  </div>
                </div>
              )}
              {([BIM_ENGINE, CAD_ENGINE].includes(engineType) || [PDF_ENGINE].includes(engineType)) &&
                !showLoader &&
                !viewerState?.pdfCompare?.active &&
                !(
                  fileVersionData &&
                  fileVersionData.fileName &&
                  fileVersionData.fileName.includes("bim_data_567365_767")
                ) &&
                // Suppressed for a recipient: FR-15 keeps the external viewer to the content itself,
                // and FR-19 forbids a side panel for a view-only model.
                !isShareView &&
                viewerState.isSidebarOpen && (
                  <Suspense fallback={<></>}>
                    <SidebarViewer
                      estimateId={urlSearchParams.get("estimateId") ? urlSearchParams.get("estimateId") : undefined}
                      fileVersionId={fileVersionId}
                    />
                  </Suspense>
                )}
              <div className="relative flex w-full" style={{ zoom: "100%" }}>
                {scheduleVersionId && newVersionTagVisible && (
                  <div className="absolute w-full mb-2 flex items-center px-4 py-2.5 bg-bg_warning border border-warning z-10">
                    <div className="flex items-center gap-x-4 flex-1">
                      <IconComponent name="FileConversionStatusInProgress" width="1.25rem" height="1.25rem" />
                      <Text>
                        {t(
                          "You are viewing a previous version of the Schedule, so this file is available in view-only mode.",
                        )}
                      </Text>
                    </div>
                    <IconComponent
                      name="close"
                      className="cursor-pointer"
                      onClick={() => setNewVersionTagVisible(false)}
                      width="0.75rem"
                      height="0.75rem"
                    />
                  </div>
                )}
                {isFileAnOlderVersion && showWarning && (
                  <div className="absolute z-[5] grid w-full grid-cols-[auto_auto] items-center justify-items-end bg-[rgb(248,0,0)] py-2.5 text-white">
                    <Text>
                      {t(
                        "You are currently working on an older version of this file. There is a newer version available.",
                      )}
                    </Text>
                    <IconComponent
                      name="Cancel"
                      onClick={handleCloseOlderVersionWarning}
                      parentContainerClass="me-4"
                      showGradient={true}
                      color="white"
                      width="1rem"
                      height="1rem"
                    />
                  </div>
                )}
                {[IMAGE_ENGINE, PANORAMA_ENGINE].includes(engineType) && (
                  <Suspense fallback={<></>}>
                    <Panorama fileVersionData={fileVersionData} />
                  </Suspense>
                )}
                {[VIDEO_ENGINE].includes(engineType) && (
                  <Suspense fallback={<></>}>
                    <Video fileVersionData={fileVersionData} />
                  </Suspense>
                )}
                {[HTML_ENGINE].includes(engineType) && (
                  <Suspense fallback={<></>}>
                    <HtmlViewer fileVersionData={fileVersionData} />
                  </Suspense>
                )}
                {embedUrl && <EmbeddedViewer embedUrl={embedUrl} />}
                {engineType == DOCUMENT_ENGINE && (
                  <div className="w-full h-full">
                    {/* Recipients get the SAME Collabora viewer (QA r5): OfficeViewer branches on the
                        share session internally and mints a READ-ONLY, version-bound WOPI token via
                        the public route. Forced on for a share regardless of OFFICE_ENABLED — the
                        legacy fallbacks below are authenticated routes a guest cannot reach; when no
                        Collabora is configured the mint declines and the download card is shown. */}
                    {OFFICE_ENABLED || isShareView ? (
                      // staging/qa: Office (Collabora) reads the source file directly (own loader)
                      premissionsData && (
                        <Suspense fallback={<></>}>
                          <OfficeViewer
                            fileId={fileId}
                            fileVersionId={fileVersionId}
                            spaceId={spaceIdParam}
                            readOnly={!premissionsData?.write}
                          />
                        </Suspense>
                      )
                    ) : (
                      // prod & other envs: legacy document viewer (conversion pipeline)
                      <>
                        {showLoader === true && (
                          <LoaderScreen
                            conversionProgress={conversionProgress}
                            fileType={fileVersionData?.awsFileName.split(".").pop()}
                          />
                        )}
                        {!showLoader && (
                          <Suspense fallback={<></>}>
                            <Document fileVersionId={fileVersionId} />
                          </Suspense>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Not for recipients: GeoSpatial immediately fetches authenticated Cesium data. */}
                {!isShareView &&
                  fileVersionData &&
                  fileVersionData.fileName &&
                  fileVersionData.fileName.includes("bim_data_567365_767") && <GeoSpatial />}

                {engineType == SHEET_ENGINE && (
                  <div className="w-full h-full">
                    {/* Recipients get the SAME Collabora viewer (QA r5): OfficeViewer branches on the
                        share session internally and mints a READ-ONLY, version-bound WOPI token via
                        the public route. Forced on for a share regardless of OFFICE_ENABLED — the
                        legacy fallbacks below are authenticated routes a guest cannot reach; when no
                        Collabora is configured the mint declines and the download card is shown. */}
                    {OFFICE_ENABLED || isShareView ? (
                      // staging/qa: Office (Collabora) reads the source file directly (own loader)
                      premissionsData && (
                        <Suspense fallback={<></>}>
                          <OfficeViewer
                            fileId={fileId}
                            fileVersionId={fileVersionId}
                            spaceId={spaceIdParam}
                            readOnly={!premissionsData?.write}
                          />
                        </Suspense>
                      )
                    ) : (
                      // prod & other envs: legacy spreadsheet viewer (conversion pipeline)
                      <>
                        {showLoader === true && (
                          <LoaderScreen
                            conversionProgress={conversionProgress}
                            fileType={fileVersionData?.awsFileName.split(".").pop()}
                          />
                        )}
                        {!showLoader && (
                          <Suspense fallback={<></>}>
                            <Spreadsheet
                              fileId={fileId}
                              fileVersionId={fileVersionId}
                              spaceId={spaceIdParam}
                              sheetProps={sheetProps}
                            />
                          </Suspense>
                        )}
                      </>
                    )}
                  </div>
                )}

                {isUnrealPlugin && (
                  <iframe
                    style={{ width: "100%", border: "none" }}
                    title={t("Unreal Integration")}
                    src="/unreal/MyProject2.html"
                  />
                )}
                {[PDF_ENGINE].includes(engineType) &&
                  viewerState?.pdfCompare?.active &&
                  viewerState.pdfCompare.revisionFileVersionId &&
                  showLoader === false && (
                    <div className="flex h-full w-full">
                      <Suspense fallback={<></>}>
                        <PdfComparisonViewer
                          baseFileVersionId={viewerState.pdfCompare.baseFileVersionId || fileVersionId}
                          revisionFileVersionId={viewerState.pdfCompare.revisionFileVersionId}
                          initialMode={viewerState.pdfCompare.initialMode}
                          initialPage={viewerState.pdfCompare.initialPage}
                          onExit={() => dispatch(setViewerState({ pdfCompare: null }))}
                        />
                      </Suspense>
                    </div>
                  )}
                {([BIM_ENGINE, CAD_ENGINE].includes(engineType) ||
                  ([PDF_ENGINE].includes(engineType) && !viewerState?.pdfCompare?.active)) &&
                  !isUnrealPlugin &&
                  !(
                    fileVersionData &&
                    fileVersionData.fileName &&
                    fileVersionData.fileName.includes("bim_data_567365_767")
                  ) && (
                    <div className="flex">
                      {showLoader === true && (
                        <LoaderScreen
                          conversionProgress={conversionProgress}
                          fileType={fileVersionData?.awsFileName.split(".").pop()}
                        />
                      )}
                      {showLoader === false && (
                        <Suspense fallback={<></>}>
                          <HoopsViewer
                            projectData={projectData}
                            fileVersionId={fileVersionId}
                            parentCallback={handleHoopsViewerCallback}
                            isMeasurementsTabOpen={isMeasurementsTabOpen}
                            selectedIssues={selectedIssues}
                            handleExportToBcf={handleExportToBcf}
                            handleDelete={handleDelete}
                            currentViewIssueId={currentViewIssueId}
                            onViewIssueClick={onViewIssueClick}
                            handleSelectedIssues={handleSelectedIssues}
                            transmittalVersionId={transmittalId}
                            estimateId={
                              urlSearchParams.get("estimateId") ? urlSearchParams.get("estimateId") : undefined
                            }
                            estimateVersionId={
                              urlSearchParams.get("versionId") ? urlSearchParams.get("versionId") : undefined
                            }
                            estimateNameId={estimateData?.estimateId}
                            estimateName={estimateData?.name}
                            scheduleVersionId={scheduleVersionId}
                            scheduleId={urlSearchParams.get("scheduleId") ?? undefined}
                            fileVersionData={fileVersionData}
                            fileType={fileType}
                            engineType={engineType}
                          />
                        </Suspense>
                      )}
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}
      {canViewFile === false && (
        <div className="flex w-full flex-col items-center justify-center gap-[3.125rem]">
          <img src={img403} alt="No Access" />
          <Text>{t("You do not have access to this page")}</Text>
        </div>
      )}
    </>
  );
}

export default DesignViewer;
